import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress as ProgressBar } from "@/components/ui/progress";
import {
  ArrowLeft, MapPin, User, Mail, Phone, Users, UserRound, Dog, CalendarDays,
  Percent, ClipboardCheck, ShieldCheck, Activity, StickyNote, Award,
} from "lucide-react";

export default function LocationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/locations/${id}/detail`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return <div className="text-center py-20 text-muted-foreground">Location not found.</div>;
  if (!d) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const loc = d.location;
  const m = d.metrics;

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/network-health")} data-testid="loc-back">
        <ArrowLeft size={16} /> Back to Network Health
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center">
            <MapPin size={22} className="text-primary-foreground" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{loc.name}</h1>
            <div className="text-sm text-muted-foreground">{loc.address} · {loc.city}, {loc.country}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge value={loc.status} />
          <span className="flex items-center gap-1.5 text-sm capitalize px-3 py-1 rounded-full bg-secondary">
            <span className={`h-2 w-2 rounded-full ${m.attention === "healthy" ? "bg-green-500" : m.attention === "warning" ? "bg-amber-500" : "bg-red-500"}`} /> {m.attention}
          </span>
        </div>
      </div>

      {/* Metric KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {[
          { icon: Users, label: "Trainers", value: d.trainers.length },
          { icon: UserRound, label: "Clients", value: d.clients.length },
          { icon: Dog, label: "Dogs", value: d.dogs.length },
          { icon: CalendarDays, label: "Upcoming", value: d.upcoming_classes.length },
          { icon: Percent, label: "Attendance", value: `${d.attendance.rate}%` },
          { icon: ClipboardCheck, label: "Homework", value: `${d.homework.rate}%` },
          { icon: ShieldCheck, label: "Quality", value: d.quality_score },
        ].map((k) => (
          <Card key={k.label} className="border-border" data-testid={`loc-kpi-${k.label.toLowerCase()}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-1.5 text-muted-foreground mb-1.5"><k.icon size={14} strokeWidth={1.5} /><span className="text-[10px] uppercase tracking-wide font-semibold">{k.label}</span></div>
              <div className="text-xl font-heading font-semibold">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Owner */}
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><User size={18} strokeWidth={1.5} /> Franchise Owner</CardTitle></CardHeader>
            <CardContent>
              <div className="font-medium">{loc.owner_name || "—"}</div>
              <div className="flex flex-wrap gap-4 mt-1.5 text-sm text-muted-foreground">
                {loc.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {loc.email}</span>}
                {loc.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {loc.phone}</span>}
              </div>
            </CardContent>
          </Card>

          {/* Trainers */}
          <ListCard title="Trainers" icon={Users} empty="No trainers." items={d.trainers} render={(t) => (
            <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60">
              <div><div className="font-medium text-sm">{t.full_name}</div><div className="text-xs text-muted-foreground">{t.specialties}</div></div>
              <StatusBadge value={t.certification_status} />
            </div>
          )} />

          {/* Dogs */}
          <ListCard title="Dogs" icon={Dog} empty="No dogs." items={d.dogs} render={(dog) => (
            <div key={dog.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60">
              <div><div className="font-medium text-sm">{dog.name}</div><div className="text-xs text-muted-foreground">{dog.breed} · {dog.trainer_name || "Unassigned"}</div></div>
              <StatusBadge value={dog.current_level} />
            </div>
          )} />

          {/* Clients */}
          <ListCard title="Clients" icon={UserRound} empty="No clients." items={d.clients} render={(c) => (
            <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60">
              <div><div className="font-medium text-sm">{c.full_name}</div><div className="text-xs text-muted-foreground">{c.email}</div></div>
              <StatusBadge value={c.status} />
            </div>
          )} />

          {/* Classes */}
          <div className="grid sm:grid-cols-2 gap-6">
            <ListCard title="Upcoming Classes" icon={CalendarDays} empty="None scheduled." items={d.upcoming_classes} render={(c) => (
              <div key={c.id} className="p-3 rounded-lg border border-border/60">
                <div className="text-sm font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(c.date)} · {c.time}</div>
              </div>
            )} />
            <ListCard title="Completed Classes" icon={CalendarDays} empty="None yet." items={d.completed_classes} render={(c) => (
              <div key={c.id} className="p-3 rounded-lg border border-border/60">
                <div className="text-sm font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(c.date)}</div>
              </div>
            )} />
          </div>
        </div>

        {/* Side column */}
        <div className="space-y-6">
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Award size={18} strokeWidth={1.5} /> Performance</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Metric label="Attendance" value={d.attendance.rate} note={`${d.attendance.present}/${d.attendance.marked} marked`} />
              <Metric label="Homework completion" value={d.homework.rate} note={`${d.homework.done}/${d.homework.total} done`} />
              <Metric label="Quality score" value={d.quality_score} note="Placeholder · HV Quality" />
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Activity size={18} strokeWidth={1.5} /> Recent Activity</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {d.recent_activity.length ? d.recent_activity.map((a, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div><div>{a.text}</div><div className="text-xs text-muted-foreground">{fmtDate(a.at)}</div></div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No recent activity.</p>}
            </CardContent>
          </Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><StickyNote size={18} strokeWidth={1.5} /> Head Office Notes</CardTitle></CardHeader>
            <CardContent>
              <div className="p-3 rounded-lg border border-dashed border-border text-sm text-muted-foreground">Location notes — coming soon.</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ListCard({ title, icon: Icon, items, render, empty }) {
  return (
    <Card className="border-border">
      <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Icon size={18} strokeWidth={1.5} /> {title} <span className="text-sm text-muted-foreground font-normal">({items.length})</span></CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {items.length ? items.map(render) : <p className="text-sm text-muted-foreground">{empty}</p>}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, note }) {
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1.5"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}{typeof value === "number" && value <= 100 ? "%" : ""}</span></div>
      <ProgressBar value={typeof value === "number" ? Math.min(value, 100) : 0} className="h-2" />
      <div className="text-xs text-muted-foreground mt-1">{note}</div>
    </div>
  );
}
